/**
 * focus-calculator.js
 * UI layer. All arithmetic lives in focus-math.js.
 */

const root = document.querySelector('[data-focus-calculator]');
if (root) init(root).catch(reportFatal);

async function init(el) {
  const math = await import(el.dataset.mathUrl);
  const data = (await import(el.dataset.equipmentUrl)).default;

  const $ = (sel) => el.querySelector(sel);
  const form = $('[data-fc-form]');
  const app = $('[data-fc-app]');

  const sensorsById = new Map(data.sensors.map((s) => [s.id, s]));

  buildReducers($('[data-fc-reducers]'), data.reducers);
  buildWavelengths($('[data-fc-wavelengths]'), data.wavelengths);
  buildTelescopes($('[data-fc-telescopes]'), data.telescopes);
  buildCameras($('[data-fc-cameras]'), data.cameras);
  buildFocusers($('[data-fc-focusers]'), data.focusers);
  buildSoftware($('[data-fc-software]'), data.software);
  buildGuideTable($('[data-fc-guide-body]'), data.software);

  // Picking a model fills focal length, aperture and obstruction. Everything
  // stays editable afterwards, so this is a convenience, never a lock-in.
  form.elements.telescope.addEventListener('change', () => {
    applyTelescope(el, form, data.telescopes, form.elements.telescope.value);
  });

  // No markup change needed: the line is created here and styled with the
  // section's existing note class.
  const specLine = document.createElement('p');
  specLine.className = 'fc__note';
  specLine.dataset.fcFocuserSpec = '';
  el.querySelector('[name="focuser"]').closest('.fc__field').after(specLine);

  applyDefaults(form, el.dataset);
  restoreCalibration(form);

  // The SCT-mode checkbox defaults to whatever the chosen motor is flagged as
  // (mirrorFocus), then stays under the user's control — so a motor adapted to
  // an SCT primary-mirror knob that we don't list as such can still be switched
  // over. Re-synced only when the motor itself changes, never on every keystroke.
  const sctBox = el.querySelector('[data-fc-sct-toggle]');
  const syncSctDefault = () => {
    const fc = data.focusers.find((x) => x.id === form.elements.focuser.value);
    if (sctBox) sctBox.checked = !!fc?.mirrorFocus;
  };
  syncSctDefault();
  form.elements.focuser.addEventListener('change', syncSctDefault);

  // Highlight the guide-table row for the selected imaging software.
  const highlightGuide = () => highlightGuideRow(el);
  highlightGuide();
  el.querySelector('[data-fc-software]')?.addEventListener('change', highlightGuide);

  // Declared before the first update() call. `function` hoists, `let` does not:
  // touching these from update() any earlier is a temporal dead zone error.
  let raf = 0;
  let lastResult = null;

  // Listen on the section, not the form: the exposure toggle lives in the
  // results column (outside the form), and input/change events bubble up here.
  el.addEventListener('input', schedule);
  el.addEventListener('change', schedule);
  window.addEventListener('resize', () => draw(lastResult), { passive: true });

  app.hidden = false;
  update();

  function schedule() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  }

  function update() {
    const f = new FormData(form);
    const focuser = data.focusers.find((x) => x.id === f.get('focuser'));
    const camera = data.cameras.find((c) => `${c.make} ${c.model}` === f.get('camera'));

    toggleConditionalFields(el, f, focuser, camera);

    // --- optics + sampling always compute -----------------------------------
    const reducer = resolveReducer(f, data.reducers);
    const pixelSize = camera ? sensorsById.get(camera.sensor).pixelSize : num(f, 'customPixelSize');
    const sensor = camera ? sensorsById.get(camera.sensor) : null;

    let base;
    try {
      base = {
        focalLength: num(f, 'focalLength'),
        aperture: num(f, 'aperture'),
        reducer,
        pixelSize,
        widthPx: sensor?.widthPx,
        heightPx: sensor?.heightPx,
        binning: num(f, 'binning'),
        wavelength: num(f, 'wavelength'),
        cfzCoefficient: num(f, 'cfzCoefficient'),
        seeing: num(f, 'seeing'),
        backlash: num(f, 'backlash') || 0,
        obstruction: readObstruction(f),
        usableTravelMm: num(f, 'usableTravel') || focuser?.travelMm || undefined,
        planOptions: {
          // Authoritative here, independent of the saved section setting so the
          // toggle can never collapse to a no-op. Ticked (default) = 7, the
          // quicker sweep; unticked = 9 for a more robust fit.
          pointCount: el.querySelector('[data-fc-expo-toggle]')?.checked === false ? 9 : 7,
          outerBlurPx: Number(el.dataset.outerBlur) || 16,
        },
      };
    } catch {
      return;
    }

    const efl = safe(() => math.effectiveFocalLength(base.focalLength, base.reducer));
    const fRatio = efl && safe(() => math.focalRatio(efl, base.aperture));
    if (!fRatio) return;

    const effectivePixel = pixelSize * base.binning;
    const scale = math.pixelScale(effectivePixel, efl);
    const cfz = math.criticalFocusZone(fRatio, base.wavelength, base.cfzCoefficient);
    const verdict = math.samplingVerdict(scale, base.seeing);

    $('[data-fc-efl]').textContent = `${efl.toFixed(0)} mm`;
    $('[data-fc-fratio]').textContent = `f/${fRatio.toFixed(2)}`;
    $('[data-fc-scale]').textContent = `${scale.toFixed(2)} ″/px`;
    $('[data-fc-cfz]').textContent = `${cfz.microns.toFixed(1)} µm`;

    const s = $('[data-fc-sampling]');
    s.textContent = `${verdict.pixelsPerFwhm.toFixed(1)} px across the seeing disc — ${verdict.state}. ${verdict.note}`;
    s.dataset.state = verdict.state.replace(/\s+/g, '-');

    // --- the focus plan needs a resolution ----------------------------------
    const umPerStep = resolveMicronsPerStep(math, focuser, f);
    const pending = $('[data-fc-pending]');
    const live = $('[data-fc-live]');

    if (!umPerStep) {
      pending.hidden = false;
      live.hidden = true;
      renderWarnings($('[data-fc-warnings]'), []);
      $('[data-fc-disclaimer]').hidden = true;
      lastResult = { cfz, umPerStep: null, fRatio, pixelSize: effectivePixel, obstruction: base.obstruction };
      draw(lastResult);
      return;
    }

    persistCalibration(f);

    const sctMode = !!(focuser.mount === 'bolt-on' && el.querySelector('[data-fc-sct-toggle]')?.checked);
    const result = math.calculate({ ...base, motor: motorFor(focuser, umPerStep), mirrorFocus: sctMode });
    const plan = result.focus;

    pending.hidden = true;
    live.hidden = false;

    // The software select lives in the results column, outside <form>, so read it
    // from the section rather than via FormData(form).
    const swVal = el.querySelector('[data-fc-software]')?.value;
    const sw = data.software.find((x) => x.id === swVal) || data.software[0];
    const met = metricFor(sw);

    $('[data-fc-stepsize]').textContent = plan.stepSize;
    $('[data-fc-points]').textContent = plan.pointCount;
    $('[data-fc-overshoot]').textContent = plan.backlashOvershoot || '—';
    $('[data-fc-umstep]').textContent = `${umPerStep.toFixed(3)} µm`;
    $('[data-fc-offset]').textContent =
      `${plan.initialOffsetSteps} steps (${(plan.initialOffsetSteps * umPerStep / 1000).toFixed(2)} mm out)`;
    $('[data-fc-cfzsteps]').textContent = plan.cfzSteps.toFixed(1);
    $('[data-fc-sweep]').textContent =
      `${plan.totalTravelSteps} steps (${(plan.totalTravelMicrons / 1000).toFixed(2)} mm)`;

    // Detail rows follow the selected software's star metric (HFR = HFD/2, etc.).
    const mdt = $('[data-fc-hfdmin-dt]'); if (mdt) mdt.textContent = `In-focus ${met.short}`;
    const odt = $('[data-fc-hfdouter-dt]'); if (odt) odt.textContent = `${met.short} at the outer exposures`;
    $('[data-fc-hfdmin]').textContent = `${(plan.hfdMin * met.factor).toFixed(2)} px`;
    $('[data-fc-hfdouter]').textContent = `${(plan.achievedOuterHfd * met.factor).toFixed(1)} px`;

    renderWarnings($('[data-fc-warnings]'), plan.warnings);
    renderPointNote($('[data-fc-pointnote]'), plan);

    // Software mapping: spell out exactly what to enter, using this rig's actual
    // numbers, so the note can never disagree with the figures above it.
    const noteEl = $('[data-fc-software-note]');
    if (noteEl) noteEl.innerHTML = softwareNote(sw, plan, umPerStep);
    $('[data-fc-disclaimer]').hidden = false;

    // CFZ at the traditional (4.88) and standard (4.0) coefficients, for the
    // optional overlay bands. The selected-coefficient band uses plan.cfzSteps.
    const cfzTypicalMicrons = math.criticalFocusZone(fRatio, base.wavelength, 4.88).microns;
    const cfzStandardMicrons = math.criticalFocusZone(fRatio, base.wavelength, 4.0).microns;
    const overlays = {
      zone: $('[data-fc-ov-zone]')?.checked !== false,
      seeing: !!$('[data-fc-ov-seeing]')?.checked,
      typical: !!$('[data-fc-ov-typical]')?.checked,
      standard: !!$('[data-fc-ov-standard]')?.checked,
    };

    lastResult = {
      cfz, umPerStep, plan, fRatio, pixelSize: effectivePixel, obstruction: base.obstruction,
      metricFactor: met.factor, metricLabel: met.label,
      cfzTypicalMicrons, cfzStandardMicrons, overlays,
    };
    draw(lastResult);
  }

  function draw(r) {
    drawCurve($('[data-fc-canvas]'), r);
  }
}

/* ------------------------- population ------------------------- */

function buildReducers(sel, reducers) {
  sel.append(...reducers.map((r) => opt(r.id, r.label, r.factor === 1)));
}

function buildWavelengths(sel, list) {
  sel.append(...list.map((w) => opt(String(w.nm), w.label, w.id === 'broadband')));
}

function buildCameras(sel, cameras) {
  const byMake = groupBy(cameras, (c) => c.make);
  for (const [make, list] of byMake) {
    const g = document.createElement('optgroup');
    g.label = make;
    g.append(...list.map((c) => opt(`${c.make} ${c.model}`, c.model)));
    sel.append(g);
  }
  sel.append(opt('__custom', 'Other — enter pixel size'));
  sel.value = 'ZWO ASI2600MC Pro';
}

/**
 * A one-line spec under the motor dropdown, with provenance.
 *
 * Steps-per-revolution never enters the arithmetic, but customers want to see
 * it, and showing where it came from is the honest way to present a figure we
 * did not measure ourselves. `null` is stated plainly rather than hidden.
 */
function focuserSpecLine(f) {
  if (!f) return '';

  const provenance = {
    mfr: 'manufacturer',
    calc: 'derived',
    sensor: 'sensor spec',
    unver: 'unverified',
    user: '',
  }[f.src] || '';
  const cite = provenance ? ` (${provenance})` : '';

  const parts = [];

  if (f.mount === 'integrated' && f.micronsPerStep) {
    parts.push(`${f.micronsPerStep} µm per step${cite}`);
    if (f.travelMm) parts.push(`${f.travelMm} mm of travel`);
    parts.push('no calibration needed');
  } else if (f.stepsPerRev) {
    parts.push(`${f.stepsPerRev.toLocaleString()} steps per revolution${cite}`);
  } else if (f.mount === 'bolt-on') {
    parts.push('steps per revolution not published');
  }

  const backlash = {
    none: 'no backlash by design',
    'none-added': 'adds no backlash of its own',
    'near-zero': 'near-zero backlash',
    low: 'low backlash',
    moderate: 'moderate backlash',
    significant: 'significant backlash — measure it',
  }[f.backlashCharacter];
  if (backlash) parts.push(backlash);

  return parts.join(' · ');
}

function buildFocusers(sel, focusers) {
  // "Other" is the escape hatch, so it belongs at the bottom of the list.
  const ordered = [...focusers].sort((a, b) =>
    (a.make === 'Other') - (b.make === 'Other'));
  const byMake = groupBy(ordered, (f) => f.make);
  for (const [make, list] of byMake) {
    const g = document.createElement('optgroup');
    g.label = make;
    g.append(
      ...list.map((f) => {
        const o = opt(f.id, f.model);
        if (f.mount === 'integrated' && f.micronsPerStep) o.textContent += ' ✓';
        return o;
      })
    );
    sel.append(g);
  }
  sel.value = 'zwo-eaf';
}

function buildSoftware(sel, list) {
  if (!sel || !list) return;
  sel.append(...list.map((s) => opt(s.id, s.name, s.id === 'nina')));
}

/** The "autofocus software guide" comparison table, built from the same data. */
function buildGuideTable(tbody, list) {
  if (!tbody || !list) return;
  const rows = list.map((s) => {
    const tr = document.createElement('tr');
    tr.dataset.swId = s.id;
    for (const val of [s.name, s.metric, s.curve, s.rule || '—']) {
      const td = document.createElement('td');
      td.textContent = val;
      tr.append(td);
    }
    return tr;
  });
  tbody.replaceChildren(...rows);
}

/** Mark the guide-table row matching the currently selected software. */
function highlightGuideRow(el) {
  const id = el.querySelector('[data-fc-software]')?.value;
  el.querySelectorAll('[data-fc-guide-body] [data-sw-id]').forEach((tr) => {
    tr.toggleAttribute('data-active', tr.dataset.swId === id);
  });
}

/* Star-measurement metric each package reports. HFR (radius) = HFD/2; HFD is a
 * diameter; FWHM is treated as diameter-like for this illustrative curve;
 * contrast software (@Focus3) is shown as star size with a note. */
const METRIC = {
  HFR: { factor: 0.5, label: 'star HFR (px)', short: 'HFR' },
  HFD: { factor: 1.0, label: 'star HFD (px)', short: 'HFD' },
  FWHM: { factor: 1.0, label: 'star FWHM (px)', short: 'FWHM' },
  Contrast: { factor: 1.0, label: 'star HFD (px)', short: 'HFD' },
};
function metricFor(sw) { return METRIC[sw && sw.metric] || METRIC.HFR; }

function esc(s) {
  return String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
}

/** The "what to type into your software" line, built from this rig's numbers. */
function softwareNote(sw, plan, umPerStep) {
  if (!sw) return '';
  const stepVal =
    sw.stepUnit === 'microns' ? `${Math.round(plan.stepSize * umPerStep)} µm`
      : sw.stepUnit === 'auto' ? `${plan.stepSize} steps (the routine sets this itself)`
        : `${plan.stepSize}`;
  const pts = sw.pointsMode === 'perSide' ? plan.pointsPerSide : plan.pointCount;

  let html = `In <strong>${esc(sw.name)}</strong>: <strong>${esc(sw.stepLabel)} = ${esc(String(stepVal))}</strong>`;
  if (sw.stepUnit !== 'auto') html += ` and <strong>${esc(sw.pointsLabel)} = ${pts}</strong>`;
  html += `. It measures <strong>${esc(metricFor(sw).short)}</strong> and fits a ${esc(sw.curve)} curve. `;
  html += `The step is the move between exposures and doesn't change with the exposure count — more exposures reach further out at the same step.`;
  if (sw.note) html += ` ${esc(sw.note)}`;
  return html;
}

function applyDefaults(form, ds) {
  if (ds.defaultSeeing) form.elements.seeing.value = ds.defaultSeeing;
  if (ds.defaultCfz) form.elements.cfzCoefficient.value = ds.defaultCfz;
}

function buildTelescopes(sel, telescopes) {
  const byMake = groupBy(telescopes, (t) => t.make);
  for (const [make, list] of byMake) {
    const g = document.createElement('optgroup');
    g.label = make;
    g.append(...list.map((t) => opt(t.id, t.model)));
    sel.append(g);
  }
  sel.value = 'refractor';
}

/**
 * Fill focal length, aperture and obstruction from a chosen model. Refractors
 * clear the obstruction; models with a value tick the box and fill the percent;
 * custom leaves the field for the user. Provenance is shown so nobody mistakes a
 * class-typical figure for one measured on their own tube.
 */
function applyTelescope(el, form, telescopes, id) {
  const t = telescopes.find((x) => x.id === id);
  if (!t) return;

  const note = el.querySelector('[data-fc-telescope-note]');
  const check = form.elements.hasObstruction;
  const field = el.querySelector('[data-fc-obstruction-field]');
  const input = form.elements.obstruction;

  if (t.focalLength) form.elements.focalLength.value = t.focalLength;
  if (t.aperture) form.elements.aperture.value = t.aperture;

  const obstructed = t.type !== 'refractor';
  check.checked = obstructed;
  field.hidden = !obstructed;
  if (t.obstruction != null) input.value = Math.round(t.obstruction * 100);

  if (note) {
    if (!obstructed) {
      note.hidden = true;
      note.textContent = '';
    } else {
      const prov =
        t.src === 'mfr' ? 'measured'
          : t.src === 'typical' ? 'typical for the class — override if you know yours'
            : '';
      const pct = t.obstruction != null ? `${Math.round(t.obstruction * 100)}% obstruction` : 'enter your obstruction below';
      note.hidden = false;
      note.textContent = [`${t.model}: ${pct}${prov ? ` (${prov})` : ''}`, t.ref]
        .filter(Boolean).join('. ') + '.';
    }
  }

  form.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Obstruction as a fraction of aperture diameter, or 0 when the box is off. */
function readObstruction(f) {
  if (!f.get('hasObstruction')) return 0;
  const pct = Number(f.get('obstruction'));
  return pct > 0 ? Math.min(0.9, pct / 100) : 0;
}

/** Show the "why this many exposures" note only when a change is worth it. */
function renderPointNote(elNote, plan) {
  if (!elNote) return;
  if (plan.pointCountNote && plan.recommendedPointCount !== plan.pointCount) {
    elNote.hidden = false;
    elNote.textContent =
      `${plan.pointCount} exposures requested — ${plan.recommendedPointCount} would suit this rig better. ${plan.pointCountNote}`;
  } else {
    elNote.hidden = true;
    elNote.textContent = '';
  }
}

/* ------------------------- resolution ------------------------- */

function resolveReducer(f, reducers) {
  const id = f.get('reducer');
  if (id === 'custom') return Number(f.get('customReducer'));
  return reducers.find((r) => r.id === id)?.factor ?? 1;
}

/**
 * Returns microns per step, or null if we genuinely cannot know it yet.
 * Never guesses. A wrong number here is worse than no number.
 *
 * Note: a bolt-on motor needs NO published steps-per-revolution. If the
 * customer measures the travel produced by a known number of steps, microns
 * per step falls straight out. stepsPerRev is used only to cross-check the
 * implied travel per revolution, where we happen to know it.
 */
function resolveMicronsPerStep(math, focuser, f) {
  if (!focuser) return null;
  if (focuser.mount === 'integrated' && focuser.micronsPerStep) return focuser.micronsPerStep;

  const steps = Number(f.get('stepsCommanded'));
  const travelMm = Number(f.get('measuredTravel'));
  if (!(steps > 0) || !(travelMm > 0)) return null;

  return (travelMm * 1000) / steps;
}

function motorFor(focuser, umPerStep) {
  return { micronsPerStep: umPerStep };
}

function toggleConditionalFields(el, f, focuser, camera) {
  const $ = (s) => el.querySelector(s);
  $('[data-fc-custom-reducer]').hidden = f.get('reducer') !== 'custom';
  $('[data-fc-custom-camera]').hidden = !!camera;
  $('[data-fc-obstruction-field]').hidden = !f.get('hasObstruction');

  // Integrated focusers publish their travel; show it as a placeholder and a
  // note so the sweep-vs-travel check runs without the user hunting for it.
  const travelInput = el.querySelector('[name="usableTravel"]');
  const travelNote = $('[data-fc-travel-note]');
  if (travelInput) {
    if (focuser?.travelMm) {
      travelInput.placeholder = `${focuser.travelMm}`;
      if (travelNote && !f.get('usableTravel')) {
        travelNote.hidden = false;
        travelNote.textContent = `Using the ${focuser.make} ${focuser.model}'s ${focuser.travelMm} mm of published travel.`;
      } else if (travelNote) {
        travelNote.hidden = true;
      }
    } else {
      travelInput.placeholder = '—';
      if (travelNote) travelNote.hidden = true;
    }
  }

  const spec = $('[data-fc-focuser-spec]');
  if (spec) spec.textContent = focuserSpecLine(focuser);

  const needsCalib = focuser && focuser.mount === 'bolt-on';
  $('[data-fc-calibration]').hidden = !needsCalib;
  if (!needsCalib) return;

  // SCT-type internal focusers move the primary mirror, not a drawtube, so the
  // number to measure is focal-plane travel. The SCT-mode checkbox drives this
  // (auto-ticked for flagged motors, user-overridable for adapted ones): swap
  // the instruction block and skip the drawtube-only travel-per-revolution
  // cross-check below (meaningless through a mirror and pulley). The measured
  // µm/step arithmetic is unchanged.
  const mirror = !!$('[data-fc-sct-toggle]')?.checked;
  const drawtubeInstr = $('[data-fc-calib-drawtube]');
  const mirrorInstr = $('[data-fc-calib-mirror]');
  if (drawtubeInstr) drawtubeInstr.hidden = mirror;
  if (mirrorInstr) mirrorInstr.hidden = !mirror;

  const steps = Number(f.get('stepsCommanded'));
  const travel = Number(f.get('measuredTravel'));
  const hint = $('[data-fc-calib-hint]');

  if (!(steps > 0) || !(travel > 0)) {
    hint.textContent = '';
    delete hint.dataset.state;
    return;
  }

  // Sanity cross-check. Where we know the motor's step count we can restate the
  // measurement as travel per revolution, which is a number the customer can
  // eyeball against their focuser. Most drawtubes move 15–40mm per turn.
  const umStep = (travel * 1000) / steps;
  if (focuser.stepsPerRev && !mirror) {
    const perRev = (umStep * focuser.stepsPerRev) / 1000;
    const odd = perRev < 5 || perRev > 60;
    hint.textContent =
      `${umStep.toFixed(3)} µm per step — implies ${perRev.toFixed(1)} mm of drawtube travel per turn` +
      (odd ? '. That is unusual; check the measurement.' : '.');
    hint.dataset.state = odd ? 'odd' : 'ok';
  } else {
    hint.textContent = mirror
      ? `${umStep.toFixed(3)} µm of focal-plane travel per step.`
      : `${umStep.toFixed(3)} µm per step.`;
    delete hint.dataset.state;
  }
}

/* ------------------------- persistence ------------------------- */

const CAL_KEY = 'ota:focus-calibration';

function persistCalibration(f) {
  const travel = Number(f.get('measuredTravel'));
  if (!(travel > 0)) return;
  try {
    localStorage.setItem(
      CAL_KEY,
      JSON.stringify({
        focuser: f.get('focuser'),
        stepsCommanded: Number(f.get('stepsCommanded')),
        measuredTravel: travel,
      })
    );
  } catch {
    /* private browsing, quota — not worth interrupting anyone over */
  }
}

function restoreCalibration(form) {
  let saved;
  try {
    saved = JSON.parse(localStorage.getItem(CAL_KEY) || 'null');
  } catch {
    return;
  }
  if (!saved) return;
  if (form.elements.focuser.querySelector(`[value="${CSS.escape(saved.focuser)}"]`)) {
    form.elements.focuser.value = saved.focuser;
  }
  form.elements.stepsCommanded.value = saved.stepsCommanded;
  form.elements.measuredTravel.value = saved.measuredTravel;
}

/* ------------------------- warnings ------------------------- */

function renderWarnings(box, warnings) {
  box.replaceChildren(
    ...warnings.map((w) => {
      const p = document.createElement('p');
      p.className = 'fc__warn';
      p.dataset.severity = w.severity;
      p.textContent = w.message;
      return p;
    })
  );
}

/* ------------------------- the V-curve -------------------------
 * Shape is illustrative — a hyperbola through a seeing-limited minimum.
 * The SAMPLE POSITIONS are real: they are exactly where the plan will
 * expose. That is the point of the drawing. If the shoulders have no
 * points on them, the curve cannot be fitted, and you can see that.
 * -------------------------------------------------------------- */

function drawCurve(canvas, r) {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 640;
  // A 2:1 canvas is 136px tall on a 360px phone — too short to read, and the
  // axis labels crowd the centre. Go taller as the canvas narrows.
  const narrow = cssW < 460;
  const cssH = Math.round(cssW * (narrow ? 0.82 : 0.56));
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  canvas.style.height = `${cssH}px`;

  const c = canvas.getContext('2d');
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, cssW, cssH);

  const style = getComputedStyle(canvas);
  // Custom properties resolve to computed colours: color-mix() arrives as
  // `rgb(195, 5, 5)`, not hex. Take whatever string the browser gives us and
  // hand it straight to the canvas, which accepts any CSS colour.
  const tok = (name, fallback) => style.getPropertyValue(name).trim() || fallback;
  const ink = tok('--fc-ink', '#14181D');
  const rule = tok('--fc-rule', '#D4DAE0');
  const paper = tok('--fc-paper', '#FFFFFF');
  const accent = tok('--fc-accent', '#C77B26');
  const brand = tok('--fc-brand', '#ED0000');
  const soft = tok('--fc-ink-soft', '#5C6672');
  const ok = tok('--fc-ok', '#2F6B4F');

  const pad = { l: 52, r: 14, t: 16, b: 42 };
  const w = cssW - pad.l - pad.r;
  const h = cssH - pad.t - pad.b;

  if (!r || !r.plan) {
    c.fillStyle = rule;
    c.font = '13px ui-monospace, monospace';
    c.textAlign = 'center';
    c.fillText('awaiting focuser measurement', cssW / 2, cssH / 2);
    return;
  }

  const { plan, umPerStep, fRatio, pixelSize } = r;
  const factor = r.metricFactor || 1;                  // HFR = 0.5, HFD = 1
  const metricLabel = r.metricLabel || 'star HFD (px)';
  const ov = r.overlays || { zone: true };

  const half = (plan.pointCount - 1) / 2;
  const spanSteps = Math.max(plan.stepSize * half * 1.18, plan.cfzSteps * 2.2, 6);

  // Same hyperbola the plan is derived from (seeing disc + geometric blur in
  // quadrature, with the donut correction), scaled to the chosen star metric.
  const obs = Math.sqrt(1 + (r.obstruction || 0) ** 2);
  const hfdMin = plan.hfdMin;
  const sizeAt = (s) => Math.hypot(hfdMin, (Math.abs(s * umPerStep) / fRatio / pixelSize) * obs);
  const valAt = (s) => sizeAt(s) * factor;

  const yMax = valAt(spanSteps) * 1.08;
  const x = (s) => pad.l + ((s + spanSteps) / (2 * spanSteps)) * w;
  const y = (v) => pad.t + h - (v / yMax) * h;

  // ---- grid + numeric ticks ----
  const xTick = Math.max(1, Math.round(niceTick(spanSteps, 5)));
  const yTick = niceTick(yMax, 4);
  c.font = '10px ui-monospace, monospace';

  c.strokeStyle = rule;
  c.lineWidth = 1;
  for (let s = xTick; s <= spanSteps + 0.5; s += xTick) {
    for (const sx of [s, -s]) { c.beginPath(); c.moveTo(x(sx), pad.t); c.lineTo(x(sx), pad.t + h); c.stroke(); }
  }
  for (let v = yTick; v <= yMax + 1e-9; v += yTick) {
    c.beginPath(); c.moveTo(pad.l, y(v)); c.lineTo(pad.l + w, y(v)); c.stroke();
  }

  // ---- overlay bands (widest first) + seeing line ----
  const band = (halfSteps, colour, alpha) => {
    if (!(halfSteps > 0)) return;
    const hs = Math.min(halfSteps, spanSteps);
    c.save(); c.globalAlpha = alpha; c.fillStyle = colour;
    c.fillRect(x(-hs), pad.t, x(hs) - x(-hs), h); c.restore();
  };
  if (ov.typical && r.cfzTypicalMicrons) band(r.cfzTypicalMicrons / umPerStep / 2, accent, 0.16);
  if (ov.standard && r.cfzStandardMicrons) band(r.cfzStandardMicrons / umPerStep / 2, ok, 0.16);
  if (ov.zone) band(plan.cfzSteps / 2, brand, 0.16);
  if (ov.seeing) {
    const py = y(valAt(0));
    c.save(); c.strokeStyle = soft; c.setLineDash([5, 4]); c.lineWidth = 1.25;
    c.beginPath(); c.moveTo(pad.l, py); c.lineTo(pad.l + w, py); c.stroke(); c.restore();
    c.fillStyle = soft; c.textAlign = 'left'; c.textBaseline = 'bottom';
    c.fillText('seeing floor', pad.l + 4, py - 2);
  }

  // ---- axis lines ----
  c.save(); c.strokeStyle = ink; c.globalAlpha = 0.75; c.lineWidth = 1;
  c.beginPath(); c.moveTo(pad.l, pad.t); c.lineTo(pad.l, pad.t + h); c.lineTo(pad.l + w, pad.t + h); c.stroke();
  c.restore();

  // ---- y tick labels ----
  c.fillStyle = soft; c.textAlign = 'right'; c.textBaseline = 'middle';
  for (let v = 0; v <= yMax + 1e-9; v += yTick) {
    c.fillText(yTick < 1 ? v.toFixed(1) : String(Math.round(v)), pad.l - 6, y(v));
  }

  // ---- curve ----
  c.strokeStyle = brand; c.lineWidth = 1.9;
  c.beginPath();
  for (let i = 0; i <= 260; i++) {
    const s = -spanSteps + (i / 260) * 2 * spanSteps;
    i ? c.lineTo(x(s), y(valAt(s))) : c.moveTo(x(s), y(valAt(s)));
  }
  c.stroke();

  // ---- sample points (the real positions) ----
  const halfCfz = plan.cfzSteps / 2;
  for (let i = -half; i <= half; i++) {
    const s = i * plan.stepSize;
    const inside = Math.abs(s) <= halfCfz;
    c.beginPath(); c.arc(x(s), y(valAt(s)), 4.5, 0, Math.PI * 2);
    c.fillStyle = inside ? brand : paper; c.strokeStyle = brand; c.lineWidth = 1.75;
    c.fill(); c.stroke();
  }

  // ---- x tick labels + axis titles ----
  c.fillStyle = soft; c.textAlign = 'center'; c.textBaseline = 'top';
  c.fillText('0', x(0), pad.t + h + 6);
  for (let s = xTick; s <= spanSteps + 0.5; s += xTick) {
    for (const sx of [s, -s]) {
      if (narrow && Math.round(sx / xTick) % 2 !== 0) continue;
      c.fillText(String(Math.round(sx)), x(sx), pad.t + h + 6);
    }
  }
  c.fillStyle = ink; c.font = '11px ui-monospace, monospace';
  c.fillText('focuser position (steps from focus)', pad.l + w / 2, pad.t + h + 22);
  c.save();
  c.translate(14, pad.t + h / 2); c.rotate(-Math.PI / 2);
  c.textAlign = 'center'; c.textBaseline = 'alphabetic';
  c.fillText(metricLabel, 0, 0);
  c.restore();
}

/** A "nice" tick spacing (1/2/5 x 10^n) so gridlines land on round numbers. */
function niceTick(range, target) {
  const raw = Math.max(range, 1e-9) / Math.max(1, target);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return nice * mag;
}

/* ------------------------- helpers ------------------------- */

function opt(value, label, selected = false) {
  const o = document.createElement('option');
  o.value = value;
  o.textContent = label;
  o.selected = selected;
  return o;
}

function groupBy(arr, fn) {
  const m = new Map();
  for (const item of arr) {
    const k = fn(item);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(item);
  }
  return m;
}

function num(f, name) {
  return Number(f.get(name));
}

function safe(fn) {
  try {
    return fn();
  } catch {
    return null;
  }
}

function reportFatal(err) {
  console.error('[focus-calculator]', err);
  const el = document.querySelector('[data-focus-calculator]');
  if (!el) return;
  const p = document.createElement('p');
  p.className = 'fc__note fc__note--error';
  p.textContent = 'The calculator failed to load. Please refresh, or contact us and we will run the numbers for you.';
  el.append(p);
}
